import type { CustomerInfo, Store } from 'react-native-purchases'

import type { UserEntitlement } from '../../../dal/user-dal'
import type { User } from '../../../hooks/useAuth0Adapter'
import { hasResolvedAccess } from '../../../services/entitlement-utils'

export type SubscriptionStore = Store | null

export interface SubscriptionStatus {
  isActive: boolean
  isCancelled: boolean
  expirationDate: Date | null
}

/**
 * Uses the same access logic as the rest of the app for `isActive`, then
 * derives cancellation/expiry details from active entitlements when available.
 */
export function getSubscriptionStatus(
  customerInfo: CustomerInfo,
  revenueCatId: string | null | undefined,
  entitlement?: UserEntitlement | null,
): SubscriptionStatus {
  const isActive = hasResolvedAccess({
    customerInfo,
    entitlement,
    revenueCatId,
  })
  const active = customerInfo.entitlements?.active ?? {}
  const entitlements = Object.values(active)

  for (const ent of entitlements) {
    if (!ent?.isActive) continue
    const isCancelled = !ent.willRenew
    const expirationDate = ent.expirationDate
      ? new Date(ent.expirationDate)
      : null
    return { isActive, isCancelled, expirationDate }
  }

  return {
    isActive,
    isCancelled: false,
    expirationDate: null,
  }
}

/**
 * Determines where the user's active subscription was purchased (RevenueCat),
 * so we can send them to the correct management flow (Stripe portal vs App Store vs Play Store).
 */
export function getSubscriptionStore(
  customerInfo: CustomerInfo,
): SubscriptionStore {
  const active = customerInfo.entitlements?.active ?? {}
  const entitlements = Object.values(active)

  for (const ent of entitlements) {
    if (!ent?.isActive) continue
    return ent.store
  }

  return null
}

export interface TimeRemaining {
  unit: 'days' | 'hours' | 'minutes'
  count: number
}

/**
 * Returns the most meaningful time unit remaining until the given date.
 * Uses days when >= 1 day, hours when >= 1 hour, otherwise minutes.
 */
export function getTimeRemaining(
  expirationDate: Date | null,
): TimeRemaining | null {
  if (expirationDate == null) return null
  const diffMs = expirationDate.getTime() - Date.now()
  if (diffMs <= 0) return null

  const totalMinutes = Math.ceil(diffMs / (1000 * 60))
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60))
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (totalDays >= 1) return { unit: 'days', count: totalDays }
  if (totalHours >= 1) return { unit: 'hours', count: totalHours }
  return { unit: 'minutes', count: Math.max(1, totalMinutes) }
}

const USER_NAME_FIELDS: (keyof User)[] = ['name', 'givenName', 'nickname']

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null
}

function getTrimmedString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function getErrorMessage(error: unknown): string | null {
  if (error instanceof Error) {
    return getTrimmedString(error.message)
  }

  if (isRecord(error)) {
    return getTrimmedString(error.message)
  }

  return null
}

export function getUserDisplayName({
  user,
  backendFirstName,
  fallbackName,
}: {
  user: User | undefined | null
  backendFirstName: string | null | undefined
  fallbackName: string
}) {
  const resolvedBackendName = getTrimmedString(backendFirstName)
  if (resolvedBackendName != null) {
    return resolvedBackendName
  }

  if (!isRecord(user)) {
    return fallbackName
  }

  for (const field of USER_NAME_FIELDS) {
    const value = getTrimmedString(user[field])
    if (value != null) {
      return value
    }
  }

  return fallbackName
}
